/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function updateGlobalHeaderHeight() {
  const header =
    document.querySelector('.cc-header') ||
    document.querySelector('store-header') ||
    document.getElementById('shopify-section-header');
  if (header) {
    const height = header.getBoundingClientRect ? header.getBoundingClientRect().height : header.offsetHeight;
    if (height > 0) {
      document.documentElement.style.setProperty('--header-height', `${height.toFixed(1)}px`);
    }
  }
}

if (typeof window !== 'undefined') {
  updateGlobalHeaderHeight();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateGlobalHeaderHeight);
  }
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;
      this.animation = this.dataset.animation || 'slide';
      this.isSticky = this.dataset.isSticky === 'true' || this.getAttribute('data-is-sticky') === 'true';

      this.bindEvents();
      this.init();
      this.initSticky();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      if (this.headerObserver) {
        this.headerObserver.disconnect();
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      // Marquee pause-on-hover is handled purely in CSS (animation-play-state),
      // so the JS pause/resume below is only relevant to the other 4 styles.
      if (this.slider && this.animation !== 'marquee' && this.animation !== 'marquee_popup') {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Initializes sticky positioning below the header
     */
    initSticky() {
      const section = this.closest('.cc-announcement') || this.parentElement;
      if (section) {
        if (this.isSticky) {
          section.classList.add('cc-announcement--sticky');
        } else {
          section.classList.remove('cc-announcement--sticky');
        }
      }

      if (!this.isSticky) return;

      updateGlobalHeaderHeight();

      this.resizeHandler = this.resizeHandler || debounce(updateGlobalHeaderHeight, 100);
      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('load', updateGlobalHeaderHeight);

      const header = document.querySelector('.cc-header') || document.querySelector('store-header');
      if (header && window.ResizeObserver) {
        this.headerObserver = new ResizeObserver(() => {
          updateGlobalHeaderHeight();
        });
        this.headerObserver.observe(header);
      }
    }

    /**
     * Start sliders a slidin'. Marquee needs no JS loop at all — it's a pure
     * CSS animation on a duplicated track — so it's skipped entirely here.
     */
    init() {
      if (this.slider && this.animation !== 'marquee' && this.animation !== 'marquee_popup') {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            // Only the "slide" style physically scrolls the track. Fade,
            // blink and pop-up just swap which slide carries .is-visible
            // and let CSS handle the transition in place.
            if (this.animation === 'slide') {
              if (document.dir === 'rtl') {
                this.slider.scrollTo(
                  -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                  0
                );
              } else {
                this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
              }
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}